import { useEffect, useState } from "react";
import { CreditCard, ArrowsLeftRight, CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin · Payment Provider settings.
 *
 * Flips the runtime toggle (admin_settings.payment_provider) between
 *   "square"  → new checkouts use Square Payment Links (active)
 *   "stripe"  → new checkouts fall back to the original Stripe Checkout flow
 *
 * Existing pending orders keep their original processor — this only affects
 * NEW /merch/checkout calls. Refunds always route back through the
 * processor stamped on the merch_order.
 */
export default function PaymentProviderPanel({ token }) {
  const [provider, setProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [squareReady, setSquareReady] = useState(null); // {ready, environment, ...}

  const load = async () => {
    setMsg("");
    const [pRes, sRes] = await Promise.all([
      fetch(`${BACKEND}/api/admin/settings/payment-provider`, { headers: { "X-Admin-Token": token } }),
      fetch(`${BACKEND}/api/admin/payments/square/status`, { headers: { "X-Admin-Token": token } }),
    ]);
    if (pRes.ok) {
      const d = await pRes.json();
      setProvider(d.provider);
    }
    if (sRes.ok) setSquareReady(await sRes.json());
  };
  useEffect(() => { load(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const flip = async (next) => {
    if (next === provider) return;
    setSaving(true);
    setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/settings/payment-provider`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ provider: next }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      const d = await r.json();
      setProvider(d.provider);
      setMsg(`Active provider is now ${d.provider === "square" ? "Square" : "Stripe"}. Existing pending orders keep their original processor.`);
    } catch (e) {
      setMsg(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 6000);
    }
  };

  const activeBadge = provider ? (
    <span className={`text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 border ${provider === "square" ? "border-[#22C55E] text-[#22C55E]" : "border-[#8A5CFF] text-[#8A5CFF]"}`}>
      Active: {provider === "square" ? "Square" : "Stripe"}
    </span>
  ) : null;

  return (
    <CollapsibleSection
      id="payment-provider"
      testId="admin-payment-provider"
      title={<><CreditCard size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />Payment Provider</>}
      subtitle="Live toggle between Square (active) and Stripe (dormant). Refunds always route back through the original processor."
      badge={activeBadge}
      onRefresh={load}
      defaultOpen
    >
      <div className="space-y-4">
        {msg && (
          <div data-testid="payment-provider-flash" className="border border-[#FF1B91]/40 bg-[#FF1B91]/5 text-white/85 text-sm px-4 py-3">
            {msg}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <ProviderCard
            id="square"
            label="Square"
            description="Square Payment Links (hosted checkout). Customer is redirected out to Square, pays, and lands back on /merch/success. Printful draft is created on payment.updated · COMPLETED."
            active={provider === "square"}
            saving={saving}
            onActivate={() => flip("square")}
            statusChip={
              squareReady?.ready ? (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#22C55E]"><CheckCircle size={12} weight="fill" /> Credentials configured · {squareReady.environment}</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-yellow-400"><Warning size={12} weight="fill" /> Waiting on SQUARE_ACCESS_TOKEN / SQUARE_LOCATION_ID / SQUARE_WEBHOOK_SIGNATURE_KEY</span>
              )
            }
          />
          <ProviderCard
            id="stripe"
            label="Stripe"
            description="Original Stripe Checkout flow (fully preserved). Every line of Stripe code stays intact — flip back at any time and merch checkout returns to Stripe with zero rebuilds."
            active={provider === "stripe"}
            saving={saving}
            onActivate={() => flip("stripe")}
            statusChip={
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#22C55E]"><CheckCircle size={12} weight="fill" /> Live keys already loaded (dormant)</span>
            }
          />
        </div>

        <div className="border border-white/10 bg-black p-4 text-[11px] uppercase tracking-[0.2em] text-white/50 leading-relaxed">
          <div className="text-white/70 mb-2 font-bold">Switch behaviour:</div>
          <ul className="space-y-1 normal-case tracking-normal text-xs">
            <li>· Only NEW <code>/api/merch/checkout</code> calls follow the new provider. Existing pending orders keep their original processor.</li>
            <li>· Refunds always route through the processor stamped on the order (badge visible on each row of Merch Orders).</li>
            <li>· Pledge donations are separate — this toggle never touches the Pledge widget.</li>
            <li>· Printful DRAFT approval mode stays exactly as set in Merch Orders → Fulfillment Mode.</li>
          </ul>
        </div>
      </div>
    </CollapsibleSection>
  );
}

function ProviderCard({ id, label, description, active, saving, onActivate, statusChip }) {
  return (
    <div
      data-testid={`payment-provider-card-${id}`}
      className={`border-2 p-5 flex flex-col gap-3 ${active ? "border-[#FF1B91] bg-[#FF1B91]/[0.06]" : "border-white/15 bg-[#0F0F0F]"}`}
    >
      <div className="flex items-center gap-3">
        <p className={`font-display font-black uppercase tracking-tight text-xl ${active ? "text-[#FF1B91]" : "text-white"}`}>{label}</p>
        {active && <span className="text-[9px] uppercase tracking-[0.25em] bg-[#FF1B91] text-white px-2 py-0.5 font-bold">Active</span>}
      </div>
      <p className="text-white/70 text-xs leading-relaxed">{description}</p>
      <div className="mt-auto flex items-center justify-between gap-2">
        {statusChip}
        <button
          type="button"
          data-testid={`payment-provider-activate-${id}`}
          disabled={active || saving}
          onClick={onActivate}
          className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border transition-colors ${active ? "border-white/10 text-white/40 cursor-not-allowed" : "border-[#FF1B91] text-white hover:bg-[#FF1B91]/20"}`}
        >
          {saving ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <ArrowsLeftRight size={12} weight="bold" />}
          {active ? "Currently active" : `Activate ${label}`}
        </button>
      </div>
    </div>
  );
}
